import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr, REGISTER_FIELDS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocumentViewer from "@/components/DocumentViewer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Download, Eye, Pencil, Library, ChevronLeft, ChevronRight, Ruler, Save } from "lucide-react";

export default function DaftarArsip({ projectId: fixedPid = null, embedded = false }) {
  const { user } = useAuth();
  const canEditMl = ["SUPER_ADMIN", "MANAGER_OPERASIONAL", "PIMPINAN_PROYEK", "STAFF_OPERASIONAL"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(fixedPid || "");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [detail, setDetail] = useState(null);
  const [detailBlob, setDetailBlob] = useState(null);
  const [mlVals, setMlVals] = useState({});
  const pageSize = 15;
  const canEdit = ["SUPER_ADMIN", "PIMPINAN_PROYEK", "STAFF_OPERASIONAL", "MANAGER_OPERASIONAL"].includes(user?.role);
  const LONG_EDIT = ["uraian_informasi", "keterangan"];
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [detailBlobType, setDetailBlobType] = useState("");
  const [detailFilename, setDetailFilename] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [masterOpts, setMasterOpts] = useState([]);
  const [editBlob, setEditBlob] = useState(null);
  const [editBlobType, setEditBlobType] = useState("");
  const [editFilename, setEditFilename] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(false);

  useEffect(() => {
    if (fixedPid) { setPid(fixedPid); return; }
    api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setPid(r.data[0].id); }).catch(() => {});
  }, [fixedPid]);

  const load = useCallback(() => {
    if (!pid) return;
    api.get(`/projects/${pid}/records?status=approved&sort=register&page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}`)
      .then((r) => setData(r.data)).catch(() => {});
  }, [pid, page, search]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/projects/${pid}/register/export?status=approved`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "daftar_arsip.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel diekspor");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDetail = async (rid) => {
    setDetailBlob(null); setDetailBlobType(""); setDetailFilename(""); setDetailError(false); setDetailLoading(true);
    try {
      const { data } = await api.get(`/projects/${pid}/records/${rid}`);
      setDetail(data);
      const docId = data.record.document_id;
      if (!docId) { setDetailLoading(false); return; }
      try {
        const docMeta = await api.get(`/projects/${pid}/documents/${docId}`);
        setDetailFilename(docMeta.data?.document?.filename || "");
      } catch (_) {}
      const r = await api.get(`/projects/${pid}/documents/${docId}/file`, { responseType: "blob" });
      setDetailBlob(URL.createObjectURL(r.data));
      setDetailBlobType(r.data.type || "");
    } catch (_) {
      setDetailError(true);
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const saveMl = async (rid) => {
    const v = mlVals[rid];
    if (v === undefined || v === "") { toast.error("Isi nilai mL"); return; }
    try {
      await api.patch(`/projects/${pid}/records/${rid}/ml`, { mL: +v });
      toast.success(`mL ${v} tersimpan`);
      setMlVals((s) => { const n = { ...s }; delete n[rid]; return n; });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openEdit = async (rec) => {
    setEditRecord(rec);
    setEditForm({ ...rec.data });
    setEditOpen(true);
    setEditBlob(null); setEditBlobType(""); setEditFilename(""); setEditError(false); setEditLoading(true);
    try {
      const r = await api.get(`/projects/${pid}/master-data/unified`);
      setMasterOpts(r.data.items || []);
    } catch (_) {}
    const docId = rec.document_id;
    if (!docId) { setEditLoading(false); return; }
    try {
      const docMeta = await api.get(`/projects/${pid}/documents/${docId}`);
      setEditFilename(docMeta.data?.document?.filename || "");
    } catch (_) {}
    try {
      const r2 = await api.get(`/projects/${pid}/documents/${docId}/file`, { responseType: "blob" });
      setEditBlob(URL.createObjectURL(r2.data));
      setEditBlobType(r2.data.type || "");
    } catch (_) {
      setEditError(true);
    } finally {
      setEditLoading(false);
    }
  };

  const applyKode = (kode) => {
    const row = masterOpts.find((o) => o.kode_klasifikasi === kode) || {};
    setEditForm((f) => ({
      ...f, kode_klasifikasi: kode,
      retensi_aktif: row.retensi_aktif ?? f.retensi_aktif,
      retensi_inaktif: row.retensi_inaktif ?? f.retensi_inaktif,
      nasib_akhir: row.nasib_akhir ?? f.nasib_akhir,
      hak_akses_internal: row.hak_akses_internal ?? f.hak_akses_internal,
      hak_akses_eksternal: row.hak_akses_eksternal ?? f.hak_akses_eksternal,
    }));
  };

  const saveEdit = async () => {
    if (!editRecord) return;
    setEditSaving(true);
    try {
      await api.patch(`/projects/${pid}/records/${editRecord.id}`, { version: editRecord.version, data: editForm });
      toast.success("Data arsip berhasil diperbarui");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Final Archive Register</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Daftar Arsip</h1>
        </div>
        <Button onClick={exportXlsx} disabled={!pid} className="rounded-full" data-testid="export-excel-btn"><Download className="h-4 w-4 mr-1.5" /> Export Excel</Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cari nomor berkas / kode / uraian..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9" data-testid="da-search" />
        </div>
        <Badge variant="secondary">{data.total} arsip disetujui</Badge>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="sticky left-0 bg-muted/50">Nomor Berkas</TableHead>
                  <TableHead>Unit Pengolah</TableHead>
                <TableHead>Kode Klasifikasi</TableHead><TableHead>Uraian Informasi</TableHead>
                <TableHead>Tahun Cipta</TableHead><TableHead>Retensi Aktif</TableHead><TableHead>Retensi Inaktif</TableHead>
                <TableHead>Nasib Akhir</TableHead><TableHead>Hak Akses Internal</TableHead><TableHead>Hak Akses Eksternal</TableHead>
                <TableHead>Disetujui Oleh</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-12"><Library className="h-8 w-8 mx-auto mb-2" />Belum ada arsip disetujui.</TableCell></TableRow>
              ) : (data.items || []).map((r) => (
                <TableRow key={r.id} data-testid={`arsip-${r.id}`}>
                  <TableCell className="font-mono text-xs sticky left-0 bg-card">{r.data.nomor_berkas || "-"}</TableCell>
                    <TableCell className="text-xs font-semibold text-primary">{r.bidang || r.unit_pengolah || r.data.bidang || r.data.unit_pengolah || "-"}</TableCell>
                  <TableCell className="text-sm font-medium">{r.data.kode_klasifikasi || "-"}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{r.data.uraian_informasi}</TableCell>
                  <TableCell className="text-sm tabular-nums">{r.data.tahun_cipta}</TableCell>
                  <TableCell className="text-sm">{r.data.retensi_aktif}</TableCell>
                  <TableCell className="text-sm">{r.data.retensi_inaktif}</TableCell>
                  <TableCell className="text-sm">{r.data.nasib_akhir}</TableCell>
                  <TableCell className="text-sm">{r.data.hak_akses_internal || "-"}</TableCell>
                  <TableCell className="text-sm">{r.data.hak_akses_eksternal || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.approved_by_name || r.disetujui_oleh || r.data?.disetujui_oleh || r.approved_by || "-"}</TableCell>
                  <TableCell className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDetail(r.id)} data-testid={`detail-${r.id}`}><Eye className="h-4 w-4" /></Button>
                    {canEdit && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground">Halaman {page} dari {totalPages}</span>
          <div className="flex gap-1.5">
            <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader><DialogTitle className="font-heading">Detail Arsip</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              {detail?.record?.ai_result && (
                <div className="rounded-lg border border-border p-3" data-testid="detail-ai">
                  <p className="text-xs font-semibold mb-1">Rekomendasi AI</p>
                  <p className="text-xs">Kode {detail?.record?.ai_result.kode_klasifikasi || "-"} · {detail?.record?.ai_result.classification_name || detail?.record?.ai_result.matched_classification || "-"} · Confidence {detail?.record?.confidence}% ({detail?.record?.confidence_level})</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{detail?.record?.ai_result.explanation}</p>
                  <p className="text-[11px] text-muted-foreground">Model: {detail?.record?.ai_result.ai_model} · {detail?.record?.ai_result.processed_at}</p>
                </div>
              )}
              <div data-testid="detail-preview">
                {detailLoading ? (
                  <div className="rounded-lg border border-border h-40 flex items-center justify-center bg-muted/20 text-sm text-muted-foreground">Memuat pratinjau...</div>
                ) : detailError ? (
                  <div className="rounded-lg border border-border h-40 flex items-center justify-center bg-muted/20 text-sm text-red-500">Dokumen tidak dapat ditampilkan.</div>
                ) : !detail?.record?.document_id ? (
                  <div className="rounded-lg border border-border h-40 flex items-center justify-center bg-muted/20 text-sm text-muted-foreground">Tidak ada dokumen untuk ditampilkan.</div>
                ) : detailBlob ? (
                  <div className="max-h-[420px] overflow-hidden">
                    <DocumentViewer url={detailBlob} type={detailBlobType} filename={detailFilename} />
                  </div>
                ) : null}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {(REGISTER_FIELDS || []).map(([k, label]) => (
                  <div key={k} className="rounded-lg border border-border p-2.5">
                    <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">{label}</p>
                    <p className="text-sm mt-0.5">{detail?.record?.data?.[k] || "-"}</p>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-semibold text-sm mb-2">Riwayat Persetujuan</p>
                <div className="space-y-1.5">
                  {(detail.history || []).map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-xs rounded-md bg-muted/40 px-3 py-2">
                      <span><Badge variant="outline" className="mr-2 text-[10px]">{h.action}</Badge>{h.user_name}{h.reason ? ` · ${h.reason}` : ""}</span>
                      <span className="text-muted-foreground tabular-nums">{new Date(h.timestamp).toLocaleString("id-ID")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(o) => !o && setEditOpen(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader><DialogTitle className="font-heading">Edit Arsip</DialogTitle></DialogHeader>
          {editRecord && (
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-2/5 md:sticky md:top-0 md:self-start" data-testid="edit-preview">
                {editLoading ? (
                  <div className="rounded-lg border border-border h-56 flex items-center justify-center bg-muted/20 text-sm text-muted-foreground">Memuat pratinjau...</div>
                ) : editError ? (
                  <div className="rounded-lg border border-border h-56 flex items-center justify-center bg-muted/20 text-sm text-red-500">Dokumen tidak dapat ditampilkan.</div>
                ) : !editRecord?.document_id ? (
                  <div className="rounded-lg border border-border h-56 flex items-center justify-center bg-muted/20 text-sm text-muted-foreground">Tidak ada dokumen untuk ditampilkan.</div>
                ) : editBlob ? (
                  <div className="max-h-[500px] overflow-hidden rounded-lg border border-border">
                    <DocumentViewer url={editBlob} type={editBlobType} filename={editFilename} />
                  </div>
                ) : null}
              </div>
              <div className="md:w-3/5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  {(REGISTER_FIELDS || []).map(([k, label]) => (
                    <div key={k} className={`space-y-1.5 ${LONG_EDIT.includes(k) ? "sm:col-span-2" : ""}`}>
                      <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">{label}</p>
                      {k === "kode_klasifikasi" ? (
                        <Select value={editForm.kode_klasifikasi || ""} onValueChange={applyKode}>
                          <SelectTrigger className="h-8 text-sm" data-testid="edit-f-kode_klasifikasi"><SelectValue placeholder="Pilih dari master data" /></SelectTrigger>
                          <SelectContent>{(masterOpts || []).map((o) => <SelectItem key={o.kode_klasifikasi} value={o.kode_klasifikasi}>{o.kode_klasifikasi} \u2014 {o.uraian}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : LONG_EDIT.includes(k) ? (
                        <Textarea rows={3} value={editForm[k] || ""} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`edit-f-${k}`} className="text-sm" />
                      ) : (
                        <Input value={editForm[k] || ""} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`edit-f-${k}`} className="h-8 text-sm" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSaving}>Batal</Button>
            <Button onClick={saveEdit} disabled={editSaving} data-testid="edit-save-btn"><Save className="h-4 w-4 mr-1.5" /> Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
